import React, { useState } from 'react';
import { disableFactor } from '../../utils/mfaApi';

/**
 * Props:
 *   factor: { factor_id, factor_type, ... }
 *   onClose(): void
 *   onDisabled(): void  — parent re-fetches factors
 */
export default function DisableFactorDialog({ factor, onClose, onDisabled }) {
  const [password, setPassword] = useState('');
  const [proof, setProof] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const proofLabel =
    factor.factor_type === 'totp' ? '6-digit code from your authenticator app'
    : factor.factor_type === 'backup_codes' ? 'One of your backup codes'
    : 'Verification code';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await disableFactor(factor.factor_id, { currentPassword: password, proof });
      onDisabled();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to disable');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Disable two-factor method
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          To confirm, enter your account password and a current verification code.
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
              {proofLabel}
            </label>
            <input
              type="text"
              value={proof}
              onChange={(e) => setProof(e.target.value)}
              required
              autoComplete="one-time-code"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono"
              disabled={submitting}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm" role="alert">
              {error}
            </div>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !password || !proof}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {submitting ? 'Disabling…' : 'Disable'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
